/**
 * Market segment resolution shared across merchandising surfaces.
 *
 * Rule:
 * - Context entities (selling product, collection, page) carry a single segment.
 *   An empty / blank / unset value resolves to the default, WOMENS.
 * - Candidate surfaces (gift wrap, GWP, UV-insert configs) carry an array of the
 *   segments they are eligible for. A single-segment candidate is just a one-element
 *   array; an empty candidate resolves to [WOMENS]. There is deliberately no "ALL"
 *   value — a candidate that should appear in more than one segment lists them
 *   explicitly.
 * - A candidate is visible when the resolved context segment is one of the
 *   candidate's resolved segments.
 */

export const SEGMENTS = ['WOMENS', 'MENS'] as const
export type MarketSegment = (typeof SEGMENTS)[number]

/** The segment an entity is treated as when nothing is set. */
export const DEFAULT_SEGMENT: MarketSegment = 'WOMENS'

/** Trim + uppercase a raw value, returning null for empty/non-string input. */
const normalise = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed ? trimmed.toUpperCase() : null
}

/**
 * Resolve a context entity's segment (selling product, collection, page).
 * Empty / blank / unset → WOMENS.
 */
export const resolveContextSegment = (raw?: string | null): string => normalise(raw) ?? DEFAULT_SEGMENT

/**
 * Resolve a candidate surface's eligible segments (gift wrap, GWP, UV insert).
 * Accepts a scalar or an array. Empty → [WOMENS].
 */
export const resolveCandidateSegments = (raw?: string | string[] | null): string[] => {
  const list = Array.isArray(raw) ? raw : raw == null ? [] : [raw]
  const resolved = list.map(normalise).filter((s): s is string => s !== null)
  return resolved.length ? resolved : [DEFAULT_SEGMENT]
}

/**
 * Whether a candidate surface should be shown in the given context.
 * @param candidate - candidate's segment(s); scalar or array, empty → [WOMENS]
 * @param context - context entity's segment; empty → WOMENS
 */
export const isCandidateVisible = (
  candidate: string | string[] | null | undefined,
  context: string | null | undefined
): boolean => resolveCandidateSegments(candidate).includes(resolveContextSegment(context))
